import React, { useEffect, useCallback, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import { actions as userActions } from "store/users";
import { useStateSelector } from "utils/field";

import SectionHeading from "components/SectionHeading";
import Input from "components/Input";
import InputCodeLib from "components/Input/CodeLibUpload";
import VerticalTabs from "components/Tabs/VerticalTabs";
import ToggleMulti from "components/ToggleMulti";
import Button from "components/Button";
import CodeList from "./CodeList";
import CodeSearchView from "./CodeSearchView";
import ExportBKCFieldDrawer from "./ExportBKCFieldDrawer";

import searchIcon from "assets/images/icons/search.svg";

import classes from "./style.module.scss";

const { fetchUserCodeLib, saveUserMeta } = userActions;

const tabs = [
    { name: "Basement Wall", url: "/", type: "BasementWall" },
    { name: "Ceiling", url: "/ceiling", type: "Ceiling" },
    { name: "Flat Ceiling", url: "/ceiling-flat", type: "CeilingFlat" },
    { name: "Crawlspace Wall", url: "/crawlspace", type: "CrawlspaceWall" },
    { name: "Floor", url: "/floor", type: "Floor" },
    { name: "Floor Header", url: "/floor-header", type: "FloorHeader" },
    { name: "Floors Above Foundation", url: "/floors-above", type: "FloorsAbove" },
    { name: "Floors Added to Slab", url: "/floors-added", type: "FloorsAdded" },
    { name: "Lintel", url: "/lintel", type: "Lintel" },
    { name: "Wall", url: "/wall", type: "Wall" },
    { name: "Window", url: "/window", type: "Window" },
];

const RValueToggle = ({ rValueUnits, handleRValueToggle }) => {
    return (
        <div className={classes.rValueToggle}>
            <span>R-Value Display</span>
            <ToggleMulti
                className={classes.toggle}
                active={rValueUnits}
                options={[
                    {
                        id: "R",
                        label: "R",
                        onClick: () => handleRValueToggle("R"),
                        className: classes.toggleOption,
                    },
                    {
                        id: "RSI",
                        label: "RSI",
                        onClick: () => handleRValueToggle("RSI"),
                        className: classes.toggleOption,
                    },
                ]}
            />
        </div>
    );
};

const escapeCSVValue = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    const stringValue = String(value);
    if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
};

export const CodeLibrary = ({ history, fetchUserCodeLib, saveUserMeta }) => {
    const [intialPath, initialTab] = window.location.pathname.split("code-library");
    const [exporting, setExporting] = useState(false);
    const [searchField, setSearchField] = useState("");
    const [isBkcDrawerOpen, setIsBkcDrawerOpen] = useState(false);

    const {
        uid,
        userMeta: { rValueUnits = "rsi" } = {},
        userPermissions: { VOLTA_DEV = false, BKC_GLIDE_APP_CONNECT = false } = {},
    } = useStateSelector(`user`) || {};

    const codeLib = useStateSelector(`user.codeLib`) || {};

    const handleUpload = useCallback(
        (data) => {
            if (!data) {
                return;
            }
            fetchUserCodeLib(uid);
        },
        [uid, fetchUserCodeLib]
    );

    const handleRValueToggle = useCallback(
        (units) => {
            saveUserMeta(uid, { rValueUnits: units });
        },
        [saveUserMeta, uid]
    );

    const handleExportCSV = useCallback(() => {
        setExporting(true);

        try {
            // CSV Headers
            const headers = [
                "Label",
                "Code",
                "Description",
                "Code_Reference",
                "Hot2000_Category",
                "Effective_R_Value",
                "ER_Min",
                "SHGC_Max",
                "U_Value_Max",
                "R_Value_Min",
            ];

            const rows = [headers.map(escapeCSVValue)];

            // Iterate through each component type (top-level keys)
            Object.keys(codeLib).forEach((componentType) => {
                const componentData = codeLib[componentType];
                const codes = componentData?.codes || {};

                // Iterate through each code in the component type
                Object.keys(codes).forEach((codeRef) => {
                    const code = codes[codeRef];
                    const isWindow = componentType === "Window";

                    //Get window specs if buried in code
                    const { layers: { window: windowOverallLayers = {}, windowLegacy: windowLegacyLayers = {} } = {} } =
                        code;
                    const {
                        shgc: windowOverallShgc,
                        overallThermalResistance: { id: thermalResId, value: windowOverallValue } = {},
                    } = windowOverallLayers[Object.keys(windowOverallLayers)[0]] || {};

                    const windowOverallUvalue =
                        thermalResId === 1 && thermalResId !== undefined
                            ? escapeCSVValue(windowOverallValue)
                            : escapeCSVValue(1 / windowOverallValue);

                    const windowOverallRvalue =
                        thermalResId === 0 && thermalResId !== undefined
                            ? escapeCSVValue(windowOverallValue)
                            : escapeCSVValue(1 / windowOverallValue);

                    const { shgc: windowLegacyShgc } = windowLegacyLayers[Object.keys(windowLegacyLayers)[0]] || {};

                    const uValueMax =
                        isWindow && windowOverallUvalue
                            ? windowOverallUvalue
                            : isWindow && code.nominalRValue && code.nominalRValue !== 0
                              ? escapeCSVValue(1 / code.nominalRValue)
                              : "";

                    const rValueMin =
                        isWindow && windowOverallRvalue
                            ? windowOverallRvalue
                            : isWindow && code.nominalRValue && code.nominalRValue !== 0
                              ? escapeCSVValue(code.nominalRValue)
                              : "";

                    const row = [
                        escapeCSVValue(code.label || ""),
                        escapeCSVValue(code.value || ""),
                        escapeCSVValue(code.description || ""),
                        escapeCSVValue(codeRef),
                        escapeCSVValue(componentType),
                        escapeCSVValue(code.effectiveRValue || ""),
                        isWindow ? escapeCSVValue(code.er || "") : "",
                        isWindow ? escapeCSVValue(windowOverallShgc || windowLegacyShgc || code.shgc || "") : "",
                        uValueMax,
                        rValueMin,
                    ];

                    rows.push(row);
                });
            });

            // Convert to CSV string
            const csvContent = rows.map((row) => row.join(",")).join("\n");

            // Create and download file
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "code_library_export.csv";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error exporting code library:", err);
        } finally {
            setExporting(false);
        }
    }, [codeLib]);

    useEffect(() => {
        fetchUserCodeLib(uid);
    }, [uid, fetchUserCodeLib]);

    const canExport = VOLTA_DEV || BKC_GLIDE_APP_CONNECT;

    const handleExportBKCField = useCallback(() => {
        setIsBkcDrawerOpen(true);
    }, []);

    return (
        <div>
            <SectionHeading
                title="Code Library"
                subtitle="Manage your standard and user defined codes."
                sideContent={
                    <div className={classes.sideContent}>
                        <Input
                            input={{
                                value: searchField,
                                onChange: (value) => setSearchField(value),
                            }}
                            icon={searchIcon}
                            placeholder="Search codes"
                            className={classes.searchInput}
                        />
                        {canExport && (
                            <>
                                <Button
                                    onClick={handleExportCSV}
                                    disabled={exporting}
                                    smallPadding
                                    className={classes.exportButton}
                                >
                                    {exporting ? "Exporting..." : "Export (.csv)"}
                                </Button>
                                <Button
                                    onClick={handleExportBKCField}
                                    disabled={exporting}
                                    smallPadding
                                    className={classes.exportButton}
                                >
                                    Export (BKC Field)
                                </Button>
                            </>
                        )}
                        <InputCodeLib
                            name="CodeLibInput"
                            id="CodeLibInput"
                            label="Upload Code Library"
                            accept=".cod,.xml"
                            handleUpload={handleUpload}
                        />
                        <RValueToggle rValueUnits={rValueUnits} handleRValueToggle={handleRValueToggle} />
                    </div>
                }
            />
            <div className={classes.contentWrapper}>
                <VerticalTabs
                    title="Component Type"
                    tabNavClassName={classes.tabNav}
                    initialTab={initialTab || "/"}
                    tabActive={initialTab || "/"}
                    hideActiveTabStyle={!!searchField.trim()}
                    tabs={tabs.map(({ name, url }) => ({ name, url }))}
                    onChange={(e) => {
                        history.push(`/code-library${e}`);
                        setSearchField("");
                    }}
                >
                    {tabs.map(({ name, type }, index) =>
                        searchField.trim() ? (
                            <CodeSearchView
                                key="search"
                                searchField={searchField}
                                tabs={tabs}
                                rValueUnits={rValueUnits}
                            />
                        ) : (
                            <CodeList key={index} name={name} type={type} rValueUnits={rValueUnits} />
                        )
                    )}
                </VerticalTabs>
            </div>
            <ExportBKCFieldDrawer
                open={isBkcDrawerOpen}
                close={() => setIsBkcDrawerOpen(false)}
                codeLib={codeLib}
            />
        </div>
    );
};

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    fetchUserCodeLib: (uid) => dispatch(fetchUserCodeLib(uid)),
    saveUserMeta: (uid, userMeta) => dispatch(saveUserMeta({ uid, userMeta })),
});

export default connect(mapStateToProps, mapDispatchToProps)(CodeLibrary);
